import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react'
import { FiRefreshCw } from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'
import { PointOfContactCard } from './PointOfContactCard'
import type { CanvasserCampaign } from '../../hooks/useGrantCampaign'

/** "Sat, Oct 3 · 9:30 AM" (or just the date when no start time). `date` is yyyy-MM-dd, read as a
 *  LOCAL calendar date — never through `new Date('yyyy-MM-dd')`, which parses as UTC midnight and
 *  shows the previous day west of Greenwich. */
export function formatShift(date: string, startTime: string | null): string {
  const [y, m, d] = date.split('-').map(Number)
  const day = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  if (!startTime) return day
  const [hh, mm] = startTime.split(':').map(Number)
  const time = new Date(2000, 0, 1, hh, mm).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${day} · ${time}`
}

interface WaitingRoomProps {
  campaign: CanvasserCampaign
  firstName: string | null
  isChecking: boolean
  isOffline: boolean
  showPhotoPrompt: boolean
  onCheckAgain: () => void
}

/**
 * [Canvasser redesign Phase 1 / spec D2, §4.2] Where a canvasser lands when they are in the campaign
 * but have no turf today. The parent polls and refetches on focus; this is only the view.
 * [Phase 7] Shows an Add-your-photo prompt when the live identity has no photo.
 */
export function WaitingRoom({ campaign, firstName, isChecking, isOffline, showPhotoPrompt, onCheckAgain }: WaitingRoomProps) {
  return (
    <VStack align="stretch" gap={4} px={4} py={6} maxW="30rem" mx="auto">
      <Box>
        <Heading size="lg" mb={1}>{firstName ? `You're in, ${firstName}` : "You're in"}</Heading>
        <Text color="fg.muted">
          You're on <Text as="span" fontWeight="semibold" color="fg">{campaign.label}</Text>. When your
          organizer gives you turf, it shows up here.
        </Text>
      </Box>

      {campaign.nextShiftDate && (
        <Box borderWidth="1px" borderColor="border" borderRadius="lg" bg="surface" p={4}>
          <Text fontWeight="semibold">Next shift: {formatShift(campaign.nextShiftDate, campaign.nextShiftStartTime)}</Text>
        </Box>
      )}

      {showPhotoPrompt && (
        <Box borderWidth="1px" borderColor="border" borderRadius="lg" bg="surface" p={4}>
          <Heading size="sm" mb={1}>Add your photo</Heading>
          <Text color="fg.muted" mb={3}>It helps your organizer know who's out on the doors.</Text>
          <Button asChild variant="outline" minH="48px">
            <RouterLink to="/canvasser/profile">Add your photo</RouterLink>
          </Button>
        </Box>
      )}

      <PointOfContactCard poc={campaign.poc} />

      {isOffline && (
        <Text fontSize="sm" color="fg.muted">
          You're offline, so we can't check for turf right now. We'll check again when you reconnect.
        </Text>
      )}

      <Button variant="outline" minH="48px" onClick={onCheckAgain} loading={isChecking}>
        <FiRefreshCw /> Check for turf
      </Button>
    </VStack>
  )
}
